import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Breadcrumb from '../components/Breadcrumb';
import Footer from '../components/Footer';
import ChatModal from '../components/ChatModal';
import FloatingChatWidget from '../components/FloatingChatWidget';
import { getChatListByUserId } from '../api/chatApi';

const ChatPage = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [chatList, setChatList] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [showChatModal, setShowChatModal] = useState(false);

  useEffect(() => {
    if (user && token) {
      loadChatList();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const loadChatList = async () => {
    try {
      setLoading(true);
      const response = await getChatListByUserId(user.id, token);
      
      if (response.success) {
        // Show all chats (both store and admin)
        setChatList(response.data || []);
      } else {
        console.error('Failed to load chat list:', response.message);
        setChatList([]);
      }
    } catch (error) {
      console.error('Error loading chat list:', error);
      setChatList([]);
    } finally {
      setLoading(false);
    }
  };

  const openChat = (chatUser) => {
    setSelectedChat(chatUser);
    setShowChatModal(true);
  };

  const closeChatModal = () => {
    setShowChatModal(false);
    setSelectedChat(null);
  };

  return (
    <>
      <Header />

      <main className="main__content_wrapper">
        <Breadcrumb
          title="Customer Support"
          items={[
            { label: 'Home', href: '/' },
            { label: 'Customer Support' }
          ]}
        />

        <section className="shop__section section--padding">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="chat__container">
                  <h2 className="chat__title mb-4">Customer Support</h2>
                  <p className="chat__subtitle text-muted mb-4">
                    Get help from our customer support team
                  </p>

                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3">Loading your chats...</p>
                    </div>
                  ) : (
                    <div className="row">
                      {/* Start Group Chat with Store & Admin */}
                      <div className="col-12 col-md-6 col-lg-4 mb-4">
                        <div
                          className="chat__user--card card h-100 cursor-pointer"
                          onClick={() => openChat({
                            id: '2,1', // Store ID 2 and Admin ID 1
                            name: 'Customer Support',
                            type: 'group',
                            isSupport: true,
                            receiver_type: 'store'
                          })}
                        >
                          <div className="card-body d-flex align-items-center">
                            <div className="chat__user--avatar me-3">
                              <img
                                src="/assets/img/logo/nav-log.png"
                                alt="Tazza Tropics"
                                className="rounded-circle"
                                style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                onError={(e) => {
                                  e.target.src = '/assets/img/product/default.png';
                                }}
                              />
                            </div>
                            <div className="chat__user--info flex-grow-1">
                              <h5 className="chat__user--name mb-1">Customer Support</h5>
                              <p className="chat__user--type text-muted mb-0">We're here to help</p>
                              <small className="text-muted">Chat with our support team</small>
                            </div>
                            <div className="chat__user--action">
                              <i className="fas fa-comments text-primary"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* No chats message */}
                      {chatList.length === 0 && !loading && (
                        <div className="col-12 text-center py-5">
                          <div className="no-chats-message">
                            <i className="fas fa-comments fa-3x text-muted mb-3"></i>
                            <h4>No previous conversations</h4>
                            <p>Click the card above to start chatting with customer support.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chat Modal */}
        {showChatModal && selectedChat && (
          <ChatModal
            chatUser={selectedChat}
            onClose={closeChatModal}
          />
        )}
      </main>

      <Footer />

      <style jsx>{`
        .cursor-pointer {
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .cursor-pointer:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .chat__user--card {
          border: 1px solid #e9ecef;
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .chat__user--card:hover {
          border-color: #007bff;
          box-shadow: 0 4px 12px rgba(0,123,255,0.1);
        }

        .chat__user--avatar img {
          border: 2px solid #f8f9fa;
        }

        .avatar-icon {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 24px;
          border: 2px solid #f8f9fa;
        }

        .admin-icon {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .store-icon {
          background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
        }

        .group-icon {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .chat__user--name {
          font-size: 16px;
          font-weight: 600;
          color: #333;
          margin-bottom: 2px;
        }

        .chat__user--type {
          font-size: 14px;
          color: #6c757d;
        }

        .chat__title {
          font-size: 28px;
          font-weight: 700;
          color: #333;
          text-align: center;
        }

        .no-chats-message {
          text-align: center;
          padding: 40px 20px;
          color: #6c757d;
        }

        .no-chats-message i {
          margin-bottom: 15px;
        }

        @media (max-width: 768px) {
          .chat__title {
            font-size: 24px;
          }

          .chat__user--card .card-body {
            padding: 15px;
          }

          .chat__user--avatar {
            margin-right: 15px;
          }

          .chat__user--avatar img {
            width: 45px;
            height: 45px;
          }
        }}
      `}</style>
      <FloatingChatWidget />
    </>
  );
};

export default ChatPage;